// ============================================================================
//  matchSavedByName — turn a saved "A, B, C" string back into picked options
// ============================================================================
//
//  Diagnoses and procedures are stored on the visit as a COMMA-JOINED LIST OF
//  NAMES — `diagnosis.Procedure_` is literally
//  `selectedProcedures.map(p => p.name).join(', ')`. So reopening a claim means
//  matching those names back against the catalogue to rebuild the chips.
//
//  Every screen did that with:
//
//      options.filter(opt => names.some(n => opt.name.toLowerCase() === n))
//
//  copy-pasted into eight places across seven files. It has two faults, and the
//  first one bites hard:
//
//  1. `filter` returns EVERY option whose name matches, not one. The procedure
//     catalogue stores one row per (procedure, ICD-10 indication) since
//     migration 042 — ORTH17 is indicated for 34 codes — so one saved name
//     re-selected up to 34 identical chips. Saving then wrote the name 34 times
//     into the column, and the next open squared it. The dropdown duplication
//     was the visible half of this; the corrupted `Procedure_` column was the
//     other half.
//
//     The server-side grouping in /loadProcedureOptions fixes the cause. This
//     fixes the consequence, and keeps it fixed for the genuine case that
//     remains: one procedure billable under two G-DRGs is two real options with
//     the same name, and a saved name must still resolve to ONE of them.
//
//  2. It returned matches in CATALOGUE order, so the chips came back in a
//     different order from the one they were saved in. Saved order is preserved
//     here — it is the order the clinician entered them, and on a claim the
//     first entry is the one that tends to be treated as principal.
//
//  Unmatched names are DROPPED, which is the existing behaviour and is the
//  right one: a name no longer in the catalogue cannot be turned back into a
//  billable row, and inventing a chip for it would let it be re-saved.
//  `matchSavedByName.unmatched()` exists for callers that want to say so.

const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();

//  Split a stored list into names. Blank entries are dropped rather than
//  becoming empty chips — a trailing comma is common in these columns.
export const splitSavedNames = (saved) => {
  if (Array.isArray(saved)) return saved.map((s) => String(s).trim()).filter(Boolean);
  return String(saved == null ? '' : saved)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

//  matchSavedByName(options, saved, [key]) -> option[]
//
//  `saved` is the stored string (or an array of names). `key` is the option
//  field holding the name; it is 'name' on every current caller.
export const matchSavedByName = (options, saved, key = 'name') => {
  const list = Array.isArray(options) ? options : [];
  const names = splitSavedNames(saved);
  if (!names.length || !list.length) return [];

  //  First option per name, built once — the alternative is a nested scan per
  //  name, and these catalogues run to hundreds of rows on screens that already
  //  re-render on every keystroke.
  const firstByName = new Map();
  for (const opt of list) {
    const k = norm(opt && opt[key]);
    if (k && !firstByName.has(k)) firstByName.set(k, opt);
  }

  const out = [];
  const taken = new Set();
  for (const n of names) {
    const hit = firstByName.get(norm(n));
    //  The same name twice in the stored string is the corruption described
    //  above. It resolves to one chip, which is what un-does it on the next save.
    if (hit && !taken.has(hit)) { taken.add(hit); out.push(hit); }
  }
  return out;
};

//  Names in the stored string that the catalogue no longer offers. Nothing calls
//  this yet; it is here so a screen can report the gap instead of a chip
//  silently disappearing on reopen.
matchSavedByName.unmatched = (options, saved, key = 'name') => {
  const list = Array.isArray(options) ? options : [];
  const have = new Set(list.map((o) => norm(o && o[key])).filter(Boolean));
  return splitSavedNames(saved).filter((n) => !have.has(norm(n)));
};

export default matchSavedByName;
